import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideServiceWorker } from '@angular/service-worker';
import { authInterceptor } from './core/auth/auth.interceptor';
import { ApplicationConfig, inject, isDevMode, provideAppInitializer } from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, RouteReuseStrategy, withInMemoryScrolling, withPreloading, PreloadAllModules } from '@angular/router';
import { provideFuse } from '@fuse';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import { appRoutes } from 'app/app.routes';
import { WorkspaceTabRouteReuseStrategy } from 'app/layout/common/workspace-tabs/workspace-tab-route-reuse.strategy';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { UserPersistenceService } from 'app/core/user/user-persistence.service';
import { AuthInitializerService } from 'app/core/auth/auth-initializer.service';
import { MockApiService } from 'app/mock-api';
import { firstValueFrom, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { BrowserRefreshBlockService } from './core/browser/browser-refresh-block.service';
import { LeaveAppConfirmService } from './core/browser/leave-app-confirm.service';
import { MatTooltipNavigationCleanupService } from './core/browser/mat-tooltip-navigation-cleanup.service';
import { GridSearchShortcutService } from './shared/components/grid-search-bar/grid-search-shortcut.service';

// 👇 NgRx Imports
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { appReducer } from './store/app.reducers';
import { AppEffects } from './store/app.effects';

// 👇 Syncfusion Imports - Removed from here (components import directly, not needed as providers)
// import { TreeViewModule, ContextMenuModule } from '@syncfusion/ej2-angular-navigations';

// Note: SessionContextService uses providedIn: 'root' so no need to provide it here

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(() => {
      inject(BrowserRefreshBlockService).initEarlyListener();
      inject(GridSearchShortcutService).initEarlyListener();
      inject(MatTooltipNavigationCleanupService).initEarlyListener();
      inject(LeaveAppConfirmService).initEarlyListener();
    }),
    provideAnimations(),

    provideHttpClient(withInterceptors([authInterceptor])),
    { provide: RouteReuseStrategy, useClass: WorkspaceTabRouteReuseStrategy },
    // Keep workflow dialogs alive across workspace tab switches / browser back (suspend+resume).
    // This object replaces Material's defaults (it is not merged with them), so focus options are
    // restated: the container takes focus on open (not the header X) and focus returns on close.
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { closeOnNavigation: false, autoFocus: 'dialog', restoreFocus: true },
    },
    provideRouter(
      appRoutes, 
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
      // Preload routes in background for faster navigation (non-breaking optimization)
      withPreloading(PreloadAllModules)
    ),
    // Provide Heroicons
    provideIcons(),
    // Material Date Adapter
    {
      provide: DateAdapter,
      useClass: LuxonDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'en-GB', // Use en-GB for DD/MM/YYYY format (British/Indian format)
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'dd/MM/yyyy', // DD/MM/YYYY format with slashes
        },
        display: {
          dateInput: 'dd/MM/yyyy', // Display: 27/11/2025 (DD/MM/YYYY)
          monthYearLabel: 'LLL yyyy',
          dateA11yLabel: 'dd/MM/yyyy',
          monthYearA11yLabel: 'LLLL yyyy',
        },
      },
    },

    // Transloco Config
    provideTransloco({
      config: {
        availableLangs: [
          { id: 'en', label: 'English' },
          { id: 'తె', label: 'తెలుగు' },
          { id: 'हि', label: 'हिन्दी' },
        ],
        defaultLang: 'en',
        fallbackLang: 'en',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    // ✅ Initialize authentication and translations in parallel (non-blocking)
    provideAppInitializer(() => {
      const authInitializer = inject(AuthInitializerService);
      const translocoService = inject(TranslocoService);
      const defaultLang = translocoService.getDefaultLang();
      
      // Start translations loading in parallel (non-blocking)
      translocoService.setActiveLang(defaultLang);
      const translationsPromise = firstValueFrom(translocoService.load(defaultLang).pipe(
        catchError(() => of(null)) // Don't block on translation errors
      ));
      
      // Wait for auth to complete, translations load in parallel
      return firstValueFrom(
        authInitializer.initialize().pipe(
          switchMap(() => translationsPromise), // Wait for translations after auth
          catchError(() => of(true)) // Don't block app load on errors
        )
      );
    }),


    // Fuse Config
    provideAuth(),

    provideFuse({
      mockApi: {
        delay: 0,
        service: MockApiService,
      },
      fuse: {
        layout: 'modern',
        scheme: 'light',
        screens: {
          sm: '600px',
          md: '960px',
          lg: '1280px',
          xl: '1440px',
        },
        theme: 'theme-default',
        themes: [
          { id: 'theme-default', name: 'Default' },
          { id: 'theme-brand', name: 'Brand' },
          { id: 'theme-teal', name: 'Teal' },
          { id: 'theme-rose', name: 'Rose' },
          { id: 'theme-purple', name: 'Purple' },
          { id: 'theme-amber', name: 'Amber' },
        ],
      },
    }),

    // NgRx Store Configuration
    provideStore(appReducer),
    provideEffects(AppEffects),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(),
    }),

    // PWA Service Worker - registers immediately after app loads
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerImmediately',
    }),
  ],
};
